import styled from "@emotion/styled";
import { FacebookEmbed, InstagramEmbed, ThreadsEmbed } from "@product/react-social-media-embed";
import type { FunctionComponent } from "react";
import { useMemo } from "react";

import { ClientSideSuspense } from "shared/components/common/client-side-suspense";

import type { ContentSchemaRenderProps } from "scmp-app/components/schema-render/content";

import {
  BlueskyContainer,
  FacebookContainer,
  InstagramContainer,
  StyledBlockquote,
  StyledBlueskyEmbed,
  StyledTikTokEmbed,
  ThreadsContainer,
  TikTokContainer,
} from "./styles";
import { BlockquoteTwitter } from "./twitter";

const types = {
  Blockquote: "blockquote",
  Bluesky: "bluesky",
  Facebook: "facebook",
  Instagram: "instagram",
  Threads: "threads",
  TikTok: "tikTok",
  Twitter: "twitter",
};

const Component: FunctionComponent<ContentSchemaRenderProps> = ({ children, ...attribs }) => {
  const url =
    attribs.schemaNode?.attribs?.["cite"] ??
    attribs.schemaNode?.attribs?.["data-instgrm-permalink"] /* cspell: disable-line */ ??
    attribs.schemaNode?.attribs?.["data-text-post-permalink"] ??
    attribs.schemaNode?.attribs?.["data-bluesky-uri"] ??
    "";

  const blockquoteType = useMemo(() => {
    switch (attribs.schemaNode.attribs?.class) {
      case "bluesky-embed":
        return types.Bluesky;
      case "fb-xfbml-parse-ignore":
        return types.Facebook;
      case "text-post-media":
        return types.Threads;
      case "tiktok-embed":
        return types.TikTok;
      case "twitter-tweet":
        return types.Twitter;
    }
    if (attribs.schemaNode.subtype === ("instagram" as SchemaSubtype)) {
      return types.Instagram;
    }
    return types.Blockquote;
  }, [attribs.schemaNode.attribs?.class, attribs.schemaNode.subtype]);

  const componentMap = {
    [types.Blockquote]: <StyledBlockquote {...attribs}>{children}</StyledBlockquote>,
    [types.Bluesky]: (
      <BlueskyContainer>
        <StyledBlueskyEmbed placeholderDisabled retryDisabled url={url} {...attribs} />
      </BlueskyContainer>
    ),
    [types.Facebook]: url && (
      <FacebookContainer>
        <ClientSideSuspense>
          <FacebookEmbed placeholderDisabled url={url} />
        </ClientSideSuspense>
      </FacebookContainer>
    ),
    [types.Instagram]: url && (
      <InstagramContainer>
        <ClientSideSuspense>
          <InstagramEmbed placeholderDisabled url={url} width={326} />
        </ClientSideSuspense>
      </InstagramContainer>
    ),
    [types.Threads]: url && (
      <ThreadsContainer>
        <ClientSideSuspense>
          <ThreadsEmbed url={url} />
        </ClientSideSuspense>
      </ThreadsContainer>
    ),
    [types.TikTok]: url && (
      <TikTokContainer>
        <StyledTikTokEmbed placeholderDisabled retryDisabled url={url} />
      </TikTokContainer>
    ),
    [types.Twitter]: <BlockquoteTwitter {...attribs} />,
  };

  return componentMap[blockquoteType];
};

Component.displayName = "Blockquote";

export const Blockquote = styled(Component)``;
